import React, { useState, useEffect, useCallback } from 'react';
import IndicatorDashboard from '../components/IndicatorDashboard';
import { fetchIndicators, fetchHistorical } from '../api';

const REFRESH_INTERVAL = 15000;

export default function Indicators() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Historical
  const [histDate, setHistDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState(null);
  const [selectedSnap, setSelectedSnap] = useState(null);
  const [mode, setMode] = useState('live');

  const loadData = useCallback(async () => {
    try {
      const res = await fetchIndicators();
      if (res.data?.data) {
        setData(res.data.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch indicators');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  const loadHistorical = useCallback(async () => {
    setHistLoading(true);
    setHistError(null);
    setHistData(null);
    setSelectedSnap(null);
    try {
      const res = await fetchHistorical(histDate);
      if (res.data?.status === 'ok') {
        setHistData(res.data);
        if (res.data.snapshots?.length > 0) {
          setSelectedSnap(res.data.snapshots[res.data.snapshots.length - 1]);
        }
      } else {
        setHistError(res.data?.message || 'Failed to load historical data');
      }
    } catch (err) {
      setHistError('Failed to load historical data');
    }
    setHistLoading(false);
  }, [histDate]);

  return (
    <div className="page-indicators">
      <div className="page-header-row">
        <h1 className="page-title">Indicators</h1>
        <div className="mode-toggle">
          <button className={`tab-btn ${mode === 'live' ? 'active' : ''}`} onClick={() => setMode('live')}>Live</button>
          <button className={`tab-btn ${mode === 'historical' ? 'active' : ''}`} onClick={() => setMode('historical')}>Historical</button>
        </div>
      </div>

      {mode === 'live' && (
        <>
          {loading && !data && (
            <div className="loading">
              <div className="spinner" />
              <p>Connecting to backend...</p>
            </div>
          )}
          {error && !data && <div className="error-banner">{error}</div>}
          {data && <IndicatorDashboard data={data} />}
        </>
      )}

      {mode === 'historical' && (
        <div className="historical-view">
          <div className="hist-controls">
            <input type="date" value={histDate} onChange={e => setHistDate(e.target.value)} />
            <button className="btn-primary" onClick={loadHistorical} disabled={histLoading}>
              {histLoading ? 'Loading...' : 'Load'}
            </button>
          </div>
          {histError && <div className="error-banner">{histError}</div>}
          {histData && histData.snapshots && (
            <>
              <div className="hist-timeline">
                <div className="timeline-bar">
                  {histData.snapshots.map((snap, i) => (
                    <button
                      key={i}
                      className={`timeline-dot ${selectedSnap === snap ? 'selected' : ''}`}
                      onClick={() => setSelectedSnap(snap)}
                      title={snap.timestamp}
                    >
                      {snap.timestamp?.split(' ')[1]?.substring(0, 5)}
                    </button>
                  ))}
                </div>
              </div>
              {selectedSnap && (
                <IndicatorDashboard data={selectedSnap} timeLabel={selectedSnap.timestamp} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
